import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBot = ({ isOpen, setIsOpen }) => {
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Hi — I'm the APS Data Technologies AI assistant. Ask me about our platforms (K12 ERP, Campus USA, Destination Aurora), our services, or how we might help your organization.",
        },
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId] = useState(
        () => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    );
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        const userMessage = input.trim();
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
        setIsLoading(true);
        try {
            const response = await axios.post(`${API}/chat`, {
                message: userMessage,
                session_id: sessionId,
            });
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: response.data.response },
            ]);
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "I'm having trouble connecting right now. Please email hr@apsdatatechnologies.com or use the contact form.",
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            {/* Floating button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 z-[9999] group flex items-center gap-3 px-5 py-3.5 rounded-full bg-accent-brand text-ink-950 font-semibold shadow-[0_10px_40px_rgba(46,144,250,0.35)] hover:scale-105 transition-transform"
                    data-testid="open-chat-button"
                >
                    <div className="relative flex items-center justify-center">
                        <MessageSquare className="w-5 h-5" strokeWidth={2} />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    </div>
                    <span className="text-sm">Ask AI</span>
                </button>
            )}

            {/* Chat window */}
            {isOpen && (
                <div
                    className="fixed bottom-6 right-6 w-[calc(100vw-3rem)] max-w-md h-[600px] max-h-[calc(100vh-3rem)] bg-ink-950 rounded-2xl border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.6)] flex flex-col z-[9999] overflow-hidden"
                    data-testid="chatbot-window"
                >
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-white/10 flex justify-between items-center bg-ink-900">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-accent-brand flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-ink-950" strokeWidth={2} />
                            </div>
                            <div>
                                <div className="text-white font-semibold text-sm">APS AI Assistant</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent-brand animate-pulse"></span>
                                    Online · GPT-5.2
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                            data-testid="close-chat-button"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
                        data-testid="chat-messages-container"
                    >
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${
                                    m.role === "user" ? "justify-end" : "justify-start"
                                }`}
                            >
                                <div
                                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-accent-brand text-ink-950 rounded-br-md"
                                            : "bg-white/[0.05] text-white/90 rounded-bl-md border border-white/5"
                                    }`}
                                    data-testid={`chat-message-${m.role}`}
                                >
                                    <p className="whitespace-pre-wrap">{m.content}</p>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-white/[0.05] px-4 py-3 rounded-2xl rounded-bl-md border border-white/5">
                                    <div className="flex gap-1">
                                        <div className="w-1.5 h-1.5 bg-accent-brand rounded-full animate-bounce"></div>
                                        <div
                                            className="w-1.5 h-1.5 bg-accent-brand rounded-full animate-bounce"
                                            style={{ animationDelay: "0.15s" }}
                                        ></div>
                                        <div
                                            className="w-1.5 h-1.5 bg-accent-brand rounded-full animate-bounce"
                                            style={{ animationDelay: "0.3s" }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="px-5 py-4 border-t border-white/10 bg-ink-900">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about our platforms..."
                                className="flex-1 px-4 py-3 bg-ink-950 border border-white/10 rounded-full text-white placeholder-white/30 text-sm focus:outline-none focus:border-accent-brand transition-colors"
                                disabled={isLoading}
                                data-testid="chat-input"
                            />
                            <button
                                onClick={handleSend}
                                disabled={isLoading || !input.trim()}
                                className="w-11 h-11 rounded-full bg-accent-brand text-ink-950 flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                data-testid="chat-send-button"
                            >
                                <Send className="w-4 h-4" strokeWidth={2} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatBot;
